import { useEffect, useState } from 'react';

import { startStreamMetadata } from './radioApi';
import { RADIO_MODE, RADIO_STATION } from './radio.config';
import { useRadioStore } from './radio.store';
import { canSkip, initRadio, pause, play, skip } from './radioEngine';
import { formatRadioItem } from './radioFormat';

/** Bieżący czas odświeżany co `intervalMs`. */
export function useNow(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);

  return now;
}

/**
 * Tytuł karty przeglądarki. W trakcie grania pokazuje bieżącą pozycję,
 * w pozostałych przypadkach nazwę podstrony i stacji.
 */
export function usePageTitle(title?: string): void {
  const playingLabel = useRadioStore(({ status, currentItem }) => {
    if (status !== 'playing' || !currentItem) return null;
    // Nazwa stacji i tak jest w tytule karty - nie powtarzamy jej jako wykonawcy.
    return currentItem.artist === RADIO_STATION.name
      ? currentItem.title
      : formatRadioItem(currentItem);
  });

  useEffect(() => {
    const parts = [playingLabel ?? title, RADIO_STATION.name].filter(Boolean);
    document.title = parts.join(' – ');
    return () => {
      document.title = RADIO_STATION.name;
    };
  }, [playingLabel, title]);
}

/** Uruchamia radio dla layoutu: silnik oraz (w trybie stream) metadane z API. */
export function useRadio(): void {
  useEffect(() => {
    initRadio();
    const stopMetadata = RADIO_MODE === 'stream' ? startStreamMetadata() : undefined;

    return () => {
      stopMetadata?.();
      // Wyjście z radia (np. do archiwum z własnym odtwarzaczem) zatrzymuje emisję
      // i zwalnia połączenie ze streamem razem z instancją hls.js.
      pause();
    };
  }, []);
}

/**
 * Ile sekund trwa bieżąca pozycja.
 * Demo: pozycja odczytana z <audio> (pauza zatrzymuje licznik).
 * Stream: różnica między zegarem a `startedAt` podanym przez serwer.
 */
export function useElapsedSeconds(): number | null {
  const position = useRadioStore((state) => state.position);
  const startedAt = useRadioStore((state) => state.currentItem?.startedAt ?? null);
  const now = useNow(1000);

  if (RADIO_MODE === 'demo') return position;
  return startedAt ? Math.max(0, (now.getTime() - Date.parse(startedAt)) / 1000) : null;
}

/** Systemowe kontrolki multimediów (ekran blokady, powiadomienie, klawisze, słuchawki). */
export function useRadioMediaSession(): void {
  const currentItem = useRadioStore((state) => state.currentItem);
  const status = useRadioStore((state) => state.status);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return undefined;
    const session = navigator.mediaSession;

    session.setActionHandler('play', play);
    session.setActionHandler('pause', pause);
    session.setActionHandler('stop', pause);
    // Prawdziwej stacji nie da się przewijać - „następny" istnieje tylko w demo.
    session.setActionHandler('nexttrack', canSkip ? skip : null);

    return () => {
      session.setActionHandler('play', null);
      session.setActionHandler('pause', null);
      session.setActionHandler('stop', null);
      session.setActionHandler('nexttrack', null);
    };
  }, []);

  const title = currentItem?.title ?? RADIO_STATION.tagline;
  const artist = currentItem?.artist ?? RADIO_STATION.name;
  const artwork = currentItem?.artwork ?? RADIO_STATION.artwork;

  useEffect(() => {
    if (!('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist,
      album: RADIO_STATION.name,
      artwork: [
        {
          src: artwork,
          ...(artwork.endsWith('.svg') ? { sizes: 'any', type: 'image/svg+xml' } : {}),
        },
      ],
    });
  }, [title, artist, artwork]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState =
      status === 'playing' || status === 'loading' || status === 'reconnecting'
        ? 'playing'
        : 'paused';
  }, [status]);
}
